import { apiClient } from "@/api/api-client";
import { Tiptap } from "@/components/common/tiptap/Tiptap";
import BottomBar from "@/components/core/actions/bottombar";
import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { useModal } from "@/components/core/modals/use-modal.ts";
import { Tabs, TabsBadge, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { articleStatuses } from "@/constants/article-statuses";
import { type Article, type ArticleFormSchema, articleFormSchema } from "@/features/dashboard/types/article.ts";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconClockHour4, IconUser, IconUserFilled } from "@tabler/icons-react";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useArticleMutation } from "../api/queries/article-query";
import ArticleRejectModal from "./article-reject-modal";
import ArticleUnpublishModal from "./article-unpublish-modal";

export default function ArticleView({ article }: { article: Article }) {
	const categoryBadges = ["bg-orange-50 text-orange-600", "bg-blue-50 text-blue-600", "bg-purple-50 text-purple-600", "bg-cyan-50 text-cyan-600"];
	const statusColors = {
		Approved: "text-green-600",
		Rejected: "text-red-600",
		Pending: "text-yellow-600",
		Unpublished: "text-orange-600",
		Draft: "text-gray-600",
	};
	const { history } = useRouter();
	const modal = useModal();
	const { user } = useAuth();
	const navigate = useNavigate();
	const articleMutation = useArticleMutation(article?.id);
	const form = useForm<ArticleFormSchema>({
		resolver: zodResolver(articleFormSchema),
		defaultValues: {
			id: article.id ? article.id : null,
			title: article.title ? article.title : "",
			body: article.body ? article.body : "",
			status: "",
			author: article.author ? article.author : "",
			remarks: "",
		},
	});

	const isSuperAdmin = Array.isArray(user?.roles) && user?.roles.some((role) => role.name === "Super Admin");

	const openRejectModal = (article: Article) => {
		modal.openModal({
			allowClose: true,
			title: "Confirm Rejection of Article",
			size: "md",
			showHeader: true,
			showFooter: false,
			component: <ArticleRejectModal data={article} />,
		});
	};

	const handleRepublish = async () => {
		await articleMutation.mutateAsync({
			status: articleStatuses.APPROVED,
			title: article.title,
			body: article.body,
			id: article.id,
			created_at: article.created_at,
			author: article.author,
			remarks: article.remarks,
			categories:
				article.categories?.map((category) => ({
					value: category.name,
					label: category.name,
				})) || [],
		});
	};

	const handleApprove = async () => {
		await articleMutation.mutateAsync({
			...article,
			status: articleStatuses.APPROVED,
			remarks: "",
			categories:
				article.categories?.map((category) => ({
					value: category.name,
					label: category.name,
				})) || [],
		});
	};

	const handleUnpublish = async () => {
		await articleMutation.mutateAsync({
			...article,
			status: articleStatuses.UNPUBLISHED,
			remarks: "",
			categories:
				article.categories?.map((category) => ({
					value: category.name,
					label: category.name,
				})) || [],
		});
	};

	const openUnpublishModal = (article: Article) => {
		modal.openModal({
			allowClose: true,
			title: "Confirm Unpublishing of Article",
			size: "md",
			showHeader: true,
			showFooter: false,
			component: <ArticleUnpublishModal data={article} />,
		});
	};

	return (
		<div className="flex flex-col w-full max-w-base p-6 gap-6">
			<div className="flex flex-row items-center justify-between w-full gap-4">
				<div className="inline-flex flex-col w-auto">
					<span className="text-xl text-foreground-dark font-semibold">Article Preview</span>
					<span className="text-base text-foreground/70">Write, publish, and manage your articles</span>
				</div>
				<div className="inline-flex flex-row w-auto gap-1">
					{/* UNPUBLISH BUTTON */}
					{isSuperAdmin && article.created_by !== user?.id && article.status === articleStatuses.APPROVED && (
						<Button type="submit" variant="danger" onClick={() => openUnpublishModal(article)}>
							Unpublish
						</Button>
					)}

					{!isSuperAdmin && article.created_by === user?.id && article.status === articleStatuses.APPROVED && (
						<Button
							type="submit"
							variant="danger"
							onClick={() => {
								modal.openModal({
									type: "confirm",
									variant: "red",
									title: "Unpublish Article",
									description: `Are you sure you want to unpublish ${article.title}?`,
									onConfirm: async () => {
										await handleUnpublish();
										// await handleApprove();
									},
								});
							}}
						>
							Unpublish
						</Button>
					)}

					{/* REPUBLISH BUTTON */}
					{(isSuperAdmin || article.created_by === user?.id) && article.status === articleStatuses.UNPUBLISHED && (
						<Button
							type="submit"
							variant="green"
							onClick={() => {
								modal.openModal({
									type: "confirm",
									variant: "green",
									title: "Republish Article",
									description: `Are you sure you want to republish ${article.title}?`,
									onConfirm: async () => {
										await handleRepublish();
									},
								});
							}}
						>
							Republish
						</Button>
					)}

					{/* APPROVE BUTTON */}
					{isSuperAdmin && article.status === articleStatuses.PENDING && (
						<Button
							type="submit"
							variant="green"
							onClick={() => {
								modal.openModal({
									type: "confirm",
									variant: "green",
									title: "Approve Article",
									description: `Are you sure you want to approve ${article.title}?`,
									onConfirm: async () => {
										await handleApprove();
									},
								});
							}}
						>
							Approve
						</Button>
					)}

					{/* REJECT BUTTON */}
					{isSuperAdmin && article.status === articleStatuses.PENDING && (
						<Button type="submit" variant="danger" onClick={() => openRejectModal(article)}>
							Reject
						</Button>
					)}
				</div>
			</div>
			<div className="flex flex-col w-full gap-4">
				<Tabs defaultValue="preview">
					<TabsList>
						<TabsTrigger value="preview">Preview</TabsTrigger>
						<TabsTrigger value="logs" disabled={user?.id !== article.created_by || !isSuperAdmin}>
							Logs
						</TabsTrigger>
					</TabsList>

					<TabsContent value="preview">
						<div className="w-full h-full flex flex-col gap-6 mt-6">
							<div className="flex justify-center">
								<div className="flex flex-col w-full max-w-[860px] mx-auto gap-8">
									<div className="flex flex-col w-full gap-2">
										<div className="flex flex-row gap-1">
											{article.categories?.map((category, catIndex) => (
												<div
													className={`inline-flex items-center h-[28px] px-2 rounded-md ${categoryBadges[catIndex] || "bg-gray-50 text-gray-600"}`}
													key={catIndex}
												>
													<span className="text-sm font-semibold">{category.label}</span>
												</div>
											))}
										</div>
										<div className="flex flex-col w-full gap-2">
											<span className="text-2xl font-bold leading-tight">{article.title}</span>
											<div className="flex flex-row gap-4 items-center">
												<div className="flex flex-row gap-2 items-center text-gray-500">
													<div className="inline-flex justify-center items-center shrink-0 w-[32px] h-[32px] rounded-full bg-slate-100">
														<IconUser stroke={2} size={18} />
													</div>
													<div className="text-base text-foreground/50">{user?.full_name}</div>
												</div>
												<div className="flex flex-row gap-2 items-center text-gray-500">
													<div className="inline-flex justify-center items-center shrink-0 w-[32px] h-[32px] rounded-full bg-slate-100">
														<IconClockHour4 stroke={2} size={18} />
													</div>
													<div className="text-base text-foreground/50">
														{article?.created_at ? formatDate(article.created_at) : new Date(Date.now()).toLocaleString()}
													</div>
												</div>
											</div>
										</div>
									</div>

									<div className="flex flex-col w-full gap-4">
										{article.coverphoto && (
											<div className="flex flex-col gap-3">
												<div className="bg-gray-300 h-full w-full">
													{article.coverphoto && !article.coverphoto.collection_name && (
														<img className="h-full w-full object-cover" src={URL.createObjectURL(article.coverphoto)} alt="Avatar" />
													)}
													{article.coverphoto && "original_url" in article.coverphoto && (
														<img className="h-full w-full object-cover" src={article.coverphoto.original_url as string} alt="Avatar" />
													)}
												</div>
												<div className="text-base text-gray-500 italic text-center mb-4">{article.caption}</div>
											</div>
										)}

										<div className="font-base text-gray-600 text-pretty">
											<span
												className="text-base text-foreground-dark"
												// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
												dangerouslySetInnerHTML={{
													__html: article.body || "",
												}}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</TabsContent>

					<TabsContent value="logs">
						<div className="my-4">
							{article?.logs?.map((log, index) => (
								<div key={index} className="flex flex-row items-center w-full rounded-md gap-2 p-2">
									<div className="flex flex-col w-full">
										<span className="text-sm text-foreground/70">{formatDate(log.created_at)}</span>
										<span className={clsx("text-sm font-bold", statusColors[log.status as keyof typeof statusColors] || "text-gray-500")}>{log.status}</span>
										<span className="text-sm">{log.remarks}</span>
									</div>
								</div>
							))}
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
